import { PIERCING_DEPOSIT_AMOUNT_EUR } from '@digitaltattoo/shared-utils';

/**
 * Tarification admin (liste + fiche RDV) : agrège piercingTypes, bijoux (actes), workflowSupplements, supplements,
 * acompte. Quand le JSON a un sous-total mais pas de `price` sur chaque prestation (données partielles), on réconcilie.
 */

export type AdminPricingLine = { label: string; amount: number };

export type SummarizeAdminPricingActInput = {
  sequence?: number | null;
  demandDetails?: Record<string, unknown> | null;
  piercingRecord?: {
    piercingTypeModel?: { id?: string | null; basePrice?: number | null } | null;
  } | null;
};

export type SummarizeAdminPricingOptions = {
  /** Bijoux / montants saisis sur les actes (parcours praticien) */
  acts?: SummarizeAdminPricingActInput[];
  /** `Appointment.depositAmount` : arrhes réellement exigées ou encaissées. */
  recordedDepositAmount?: number | null;
  appointmentType?: string | null;
};

export type AdminAppointmentPricingSummary = {
  lines: AdminPricingLine[];
  piercingSubtotal: number;
  jewelryTotal: number;
  workflowSupplementsTotal: number;
  legacySupplementsTotal: number;
  deposit: number;
  /** Sous-total lignes (prestations + bijoux + produits facturés praticien + anciens suppléments) */
  computedSubtotal: number;
  /** Reste typique après acompte (comme caisse / finalisation) */
  balanceAfterDeposit: number;
  /** Solde après acompte affiché dans la carte */
  displayTotal: number;
  basePrice: number;
  storedTotal: number;
  storedSubtotal: number;
  currency: string;
  paymentMethod: string;
};

function asRecord(p: unknown): Record<string, unknown> {
  return p && typeof p === 'object' && !Array.isArray(p) ? (p as Record<string, unknown>) : {};
}

function numMoney(v: unknown): number {
  if (v == null) return 0;
  const n = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Montant unitaire d’une ligne prestation piercing (plusieurs clés selon la source des données). */
function piercingRowAmount(pt: Record<string, unknown>): number {
  const keys = ['price', 'customPrice', 'amount', 'unitPrice', 'piercingPrice', 'basePrice'] as const;
  for (const k of keys) {
    const n = Math.max(0, numMoney(pt[k]));
    if (n > 0) return n;
  }
  return 0;
}

function sumLines(lines: AdminPricingLine[]): number {
  return lines.reduce((s, l) => s + Math.max(0, l.amount), 0);
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Arrhes affichées.
 * La colonne `depositAmount` prime : c’est le montant exigé ou déjà payé.
 * Sinon, le formulaire backoffice a souvent enregistré 50 % du prix dans `pricing.deposit`.
 * Pour un piercing, ce pourcentage n’est pas le barème : les arrhes sont un montant fixe.
 */
function resolveDisplayedDeposit(
  pricing: Record<string, unknown>,
  options: SummarizeAdminPricingOptions | undefined,
  bookingReference: number,
): number {
  const recorded = options?.recordedDepositAmount;
  if (recorded != null && Number.isFinite(Number(recorded))) {
    return roundMoney(Math.max(0, Number(recorded)));
  }

  const pricingDeposit = numMoney(pricing.deposit);
  const pricingDepositAmount = numMoney(pricing.depositAmount);
  let deposit = pricingDeposit > 0 ? pricingDeposit : pricingDepositAmount;

  if (options?.appointmentType === 'PIERCING' && bookingReference > 0) {
    const half = roundMoney(bookingReference * 0.5);
    if (Math.abs(deposit - half) <= 0.02) {
      deposit = PIERCING_DEPOSIT_AMOUNT_EUR;
    }
  }

  return roundMoney(Math.max(0, deposit));
}

function actPiercingTypeId(act: SummarizeAdminPricingActInput): string | undefined {
  const dd = (act.demandDetails ?? {}) as Record<string, unknown>;
  const raw = dd.piercingTypeModelId ?? act.piercingRecord?.piercingTypeModel?.id;
  if (raw == null) return undefined;
  const id = String(raw).trim();
  return id.length > 0 ? id : undefined;
}

function actModelBasePrice(act: SummarizeAdminPricingActInput): number {
  return Math.max(0, numMoney(act.piercingRecord?.piercingTypeModel?.basePrice));
}

function pushJewelryLinesFromActs(
  acts: SummarizeAdminPricingActInput[] | undefined,
): { lines: AdminPricingLine[]; total: number } {
  const lines: AdminPricingLine[] = [];
  let total = 0;
  if (!Array.isArray(acts)) return { lines, total };
  for (const act of acts) {
    const j = act.demandDetails?.jewelry as Record<string, unknown> | undefined;
    if (!j || typeof j !== 'object') continue;
    const price = Math.max(0, numMoney(j.price));
    if (price <= 0) continue;
    const material = j.material != null ? String(j.material) : '';
    const name = material ? `Bijou — ${material}` : `Bijou (acte ${act.sequence ?? '?'})`;
    lines.push({ label: name, amount: price });
    total += price;
  }
  return { lines, total };
}

/**
 * Si le sous-total stocké (réservation) est supérieur au détail calculé, répartir le manque
 * sur les lignes prestation sans prix (même logique que la caisse employé).
 */
function reconcilePiercingLineAmounts(
  lines: AdminPricingLine[],
  piercingLineCount: number,
  storedReference: number,
  computedBefore: number,
): void {
  if (storedReference <= 0 || piercingLineCount <= 0) return;
  const missing = storedReference - computedBefore;
  if (missing <= 0.02) return;

  const PierceIndices: number[] = [];
  for (let i = 0; i < Math.min(piercingLineCount, lines.length); i++) PierceIndices.push(i);
  const zeroIdx = PierceIndices.filter((i) => lines[i] && lines[i].amount < 0.005);
  if (zeroIdx.length > 0) {
    const each = missing / zeroIdx.length;
    for (const i of zeroIdx) {
      lines[i].amount = Math.max(0, lines[i].amount + each);
    }
    return;
  }

  /** Déjà des montants par ligne mais total encore en dessous du JSON (ex. arrondis) */
  const each = missing / PierceIndices.length;
  for (const i of PierceIndices) {
    lines[i].amount = Math.max(0, lines[i].amount + each);
  }
}

export function summarizeAdminAppointmentPricing(
  pricing: Record<string, unknown> | null | undefined,
  options?: SummarizeAdminPricingOptions,
): AdminAppointmentPricingSummary {
  const p = asRecord(pricing);
  const acts = options?.acts;

  const ptsRaw = p.piercingTypes;
  const pts = Array.isArray(ptsRaw) ? (ptsRaw as Array<Record<string, unknown>>) : [];
  const single = p.piercingType && typeof p.piercingType === 'object' && !Array.isArray(p.piercingType);
  const piercingTypes = pts.length > 0 ? pts : single ? [p.piercingType as Record<string, unknown>] : [];

  const lines: AdminPricingLine[] = [];
  let piercingSubtotal = 0;
  const actsByTypeId = new Map<string, SummarizeAdminPricingActInput>();
  if (Array.isArray(acts)) {
    for (const act of acts) {
      const typeId = actPiercingTypeId(act);
      if (typeId && !actsByTypeId.has(typeId)) actsByTypeId.set(typeId, act);
    }
  }

  piercingTypes.forEach((pt, idx) => {
    let price = piercingRowAmount(pt);
    if (price <= 0 && Array.isArray(acts)) {
      const ptIdRaw = pt.id ?? pt.piercingTypeModelId ?? pt.piercingTypeId;
      const ptId = ptIdRaw == null ? undefined : String(ptIdRaw);
      const actByType = ptId ? actsByTypeId.get(ptId) : undefined;
      const actByIndex = idx < acts.length ? acts[idx] : undefined;
      const fallback = Math.max(
        actByType ? actModelBasePrice(actByType) : 0,
        actByIndex ? actModelBasePrice(actByIndex) : 0,
      );
      if (fallback > 0) price = fallback;
    }
    const name = String(pt?.name ?? `Prestation ${idx + 1}`);
    lines.push({ label: name, amount: price });
    piercingSubtotal += price;
  });

  const { lines: jewelryLines, total: jewelryTotal } = pushJewelryLinesFromActs(acts);
  lines.push(...jewelryLines);

  const workflowRaw = p.workflowSupplements;
  const workflow = Array.isArray(workflowRaw) ? (workflowRaw as Array<Record<string, unknown>>) : [];
  let workflowSupplementsTotal = 0;
  for (const w of workflow) {
    if (!w) continue;
    const name = String(w.name ?? w.label ?? w.productCode ?? w.reference ?? '').trim();
    const qty = Math.max(1, Math.floor(Number(w.qty) || 1));
    const amountValue = Math.max(0, numMoney(w.amount));
    const unit = Math.max(0, numMoney(w.price));
    // Prefer persisted line total when provided; fallback to unit x qty.
    const amt = amountValue > 0 ? amountValue : unit * qty;
    if (!name && amt <= 0) continue;
    lines.push({ label: qty > 1 ? `${name} × ${qty}` : name, amount: amt });
    workflowSupplementsTotal += amt;
  }

  const legacyRaw = p.supplements;
  const legacy = Array.isArray(legacyRaw) ? (legacyRaw as Array<Record<string, unknown>>) : [];
  let legacySupplementsTotal = 0;
  for (const s of legacy) {
    const qty = Math.max(1, Math.floor(Number(s?.qty) || 1));
    const amountValue = Math.max(0, numMoney(s?.amount));
    const unit = Math.max(0, numMoney(s?.price));
    // Legacy entries may persist either a line total (amount) or a unit price (price).
    const amt = amountValue > 0 ? amountValue : unit * qty;
    const name = String(s?.name ?? s?.label ?? s?.productCode ?? s?.reference ?? '').trim();
    if (!name && amt === 0) continue;
    lines.push({ label: qty > 1 ? `${name || 'Supplément'} × ${qty}` : (name || 'Supplément'), amount: amt });
    legacySupplementsTotal += amt;
  }

  const basePrice = Number(p.basePrice ?? 0) || 0;
  const storedTotal = numMoney(p.total);
  const storedSubtotal = numMoney(p.subtotal);

  let computedSubtotal = sumLines(lines);

  /** Montant « prévu » à la réservation : sous-total / total / base, le plus fiable en premier */
  const storedReference =
    storedSubtotal > 0 ? storedSubtotal : storedTotal > 0 ? storedTotal : basePrice > 0 ? basePrice : 0;

  if (piercingTypes.length > 0 && storedReference > 0) {
    reconcilePiercingLineAmounts(lines, piercingTypes.length, storedReference, computedSubtotal);
  }

  /** Fallback : aucune ligne mais basePrice / sous-total connu */
  if (lines.length === 0 && storedReference > 0) {
    lines.push({ label: 'Montant réservation (détail non ventilé)', amount: storedReference });
  } else if (lines.length === 0 && basePrice > 0) {
    lines.push({ label: 'Indicatif réservation', amount: basePrice });
  }

  piercingSubtotal = sumLines(lines.slice(0, piercingTypes.length));
  computedSubtotal = sumLines(lines);

  /** Sous-total JSON sans lignes de prestation (ex. tableau piercingTypes vidé ou incomplet). */
  if (piercingTypes.length === 0 && storedReference > computedSubtotal + 0.02) {
    lines.push({
      label: 'Montant réservation (hors détail)',
      amount: storedReference - computedSubtotal,
    });
    computedSubtotal = sumLines(lines);
  }

  const deposit = resolveDisplayedDeposit(p, options, storedReference || computedSubtotal);

  const balanceAfterDeposit = Math.max(0, computedSubtotal - deposit);

  const displayTotal =
    lines.length > 0 ? balanceAfterDeposit : Math.max(0, storedTotal, balanceAfterDeposit);

  return {
    lines,
    piercingSubtotal,
    jewelryTotal,
    workflowSupplementsTotal,
    legacySupplementsTotal,
    deposit,
    computedSubtotal,
    balanceAfterDeposit,
    displayTotal,
    basePrice,
    storedTotal,
    storedSubtotal,
    currency: String(p.currency ?? 'EUR'),
    paymentMethod: String(p.paymentMethod ?? '—'),
  };
}

/** Texte pour tooltip liste RDV */
export function pricingSummaryToTooltipLines(
  summary: AdminAppointmentPricingSummary,
  formatMoney: (n: number) => string,
): string[] {
  const out: string[] = [];
  summary.lines.forEach((l) => {
    out.push(`${l.label}: ${formatMoney(l.amount)}`);
  });
  if (summary.deposit > 0) {
    out.push(`Acompte: −${formatMoney(summary.deposit)}`);
  }
  out.push(`Total affiché: ${formatMoney(summary.displayTotal)}`);
  if (summary.storedTotal > 0 && Math.abs(summary.storedTotal - summary.displayTotal) > 0.02) {
    out.push(`(Total stocké résa: ${formatMoney(summary.storedTotal)})`);
  }
  return out;
}
